import { useMemo } from "react";

import {
  ExperienceTestTypeDto,
  GenericExperienceFe,
  WebUiScopesConfigurationKeyType,
} from "@intelligems/ig-types";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useBooleanWebUiScopeMap } from "@src/components/LimitAccess/check-scopes-hooks";
import { useCheckSubscriptionStatus } from "@src/components/LimitAccess/check-subscriptions-hooks";

const CreateTestTypeMap: Record<
  keyof ExperienceTestTypeDto,
  Lowercase<WebUiScopesConfigurationKeyType>
> = {
  hasTestPricing: "create_price_experiment",
  hasTestShipping: "create_shipping_experiment",
  hasTestContentTemplate: "create_template_experiment",
  hasTestContentTheme: "create_theme_experiment",
  hasTestContent: "create_onsite_edits_experiment",
  hasTestCampaign: "create_discount_experiment",
  hasTestContentAdvanced: "create_content_experiment", // deprecated?
  hasTestContentOnsite: "create_onsite_edits_experiment",
  hasTestContentUrl: "create_redirect_experiment",
  hasTestOnsiteInjections: "create_onsite_edits_experiment",
};

const StartTestTypeMap: Record<
  keyof ExperienceTestTypeDto,
  Lowercase<WebUiScopesConfigurationKeyType>
> = {
  hasTestPricing: "start_price_experiment",
  hasTestShipping: "start_shipping_experiment",
  hasTestContentTemplate: "start_template_experiment",
  hasTestContentTheme: "start_theme_experiment",
  hasTestContent: "start_onsite_edits_experiment",
  hasTestCampaign: "start_offer",
  hasTestContentAdvanced: "start_content_experiment", // deprecated?
  hasTestContentOnsite: "start_onsite_edits_experiment",
  hasTestContentUrl: "start_redirect_experiment",
  hasTestOnsiteInjections: "start_onsite_edits_experiment",
};

const FriendlyTestTypeMap: Record<keyof ExperienceTestTypeDto, string> = {
  hasTestPricing: "pricing",
  hasTestShipping: "shipping",
  hasTestContentTemplate: "template",
  hasTestContentTheme: "theme",
  hasTestContent: "onsite edits",
  hasTestCampaign: "discount",
  hasTestContentAdvanced: "content", // deprecated?
  hasTestContentOnsite: "onsite edits",
  hasTestContentUrl: "redirect",
  hasTestOnsiteInjections: "onsite edits",
};

export const useIsShippingTestAlreadyActive = () => {
  const experiencesList = useGetExperiencesList({ category: "experiment" });

  return useMemo(
    () =>
      (experiencesList.data?.experiencesList
        ?.filter((experience) => experience.status === "started")
        ?.filter((experience) => experience.testTypes.hasTestShipping)
        ?.length || 0) > 0,
    [experiencesList]
  );
};

export const useIsTestLimitHit = () => {
  const experiencesList = useGetExperiencesList({ category: "experiment" });
  const org = useGetOrg();

  return useMemo(() => {
    const maxTests: number = org.data.org.configurations.find(
      (c) => c.key === "concurrent_tests"
    )?.value;
    const activeTestsCount =
      experiencesList.data?.experiencesList.filter(
        (ex) => ex.status === "started"
      ).length || 0;
    return activeTestsCount >= maxTests;
  }, [experiencesList]);
};

export const useIsPriceTestIntegrationReady = () => {
  const organization = useGetOrg();
  const isHeadless = useConfiguration("is_headless_storefront");
  const priceSelectors = useConfiguration<string[]>(
    "additional_price_query_selectors"
  );

  return useMemo(
    () =>
      Boolean(
        organization.data.org.integrationStatus === "integration_complete" ||
          (priceSelectors?.value || []).length > 0 ||
          isHeadless?.value
      ),
    [organization.data.org.integrationStatus, priceSelectors, isHeadless?.value]
  );
};

export const useCanStartExperience = (
  experience: GenericExperienceFe
): { canStart: boolean; disabledMessage: string } => {
  const isSubscriptionActive = useCheckSubscriptionStatus();
  const scopeMap = useBooleanWebUiScopeMap();
  const isPriceTestIntegrationReady = useIsPriceTestIntegrationReady();
  const isShippingTestAlreadyActive = useIsShippingTestAlreadyActive();
  const isTestLimitHit = useIsTestLimitHit();

  return useMemo(() => {
    const category =
      experience.category === "experiment" ? "test" : "personalization";

    if (!experience) {
      return { canStart: false, disabledMessage: "" };
    }

    if (!isSubscriptionActive) {
      return {
        canStart: false,
        disabledMessage: `Approve billing to start this ${category}`,
      };
    }

    const missingTestTypes = Object.entries(experience.testTypes)
      .map(([key, value]) => {
        if (value) {
          const scopeKey = StartTestTypeMap[key as keyof ExperienceTestTypeDto];
          const result = scopeMap[scopeKey];
          if (!result) {
            return FriendlyTestTypeMap[key as keyof ExperienceTestTypeDto];
          }
        }
        return null;
      })
      .filter((s): s is string => Boolean(s));

    const hasNecessaryScopes = missingTestTypes.length === 0;

    if (isTestLimitHit) {
      return {
        canStart: false,
        disabledMessage: `You have reached the maximum number of active tests. End an existing test to start a new one.`,
      };
    }

    if (!hasNecessaryScopes) {
      return {
        canStart: false,
        disabledMessage: `Upgrade your plan to start this ${missingTestTypes.join(", ")} ${category}`,
      };
    }

    if (experience.testTypes.hasTestPricing) {
      if (!isPriceTestIntegrationReady) {
        return {
          canStart: false,
          disabledMessage: `Complete your pricing integration to start this ${category}`,
        };
      }
    }

    if (experience.testTypes.hasTestShipping) {
      if (isShippingTestAlreadyActive && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: `Only one shipping test can be run at a time. End your current shipping test to start a new one.`,
        };
      }
    }
    return {
      canStart: true,
      disabledMessage: "",
    };
  }, [
    experience.id,
    isSubscriptionActive,
    scopeMap,
    isPriceTestIntegrationReady,
    isShippingTestAlreadyActive,
    isTestLimitHit,
  ]);
};

export const useCanCrateExperience = (
  experience: GenericExperienceFe
): { canCreate: boolean; disabledMessage: string } => {
  const isSubscriptionActive = useCheckSubscriptionStatus();
  const scopeMap = useBooleanWebUiScopeMap();
  const isPriceTestIntegrationReady = useIsPriceTestIntegrationReady();
  return useMemo(() => {
    const category =
      experience.category === "experiment" ? "test" : "personalization";

    if (!experience) {
      return { canCreate: false, disabledMessage: "" };
    }

    if (!isSubscriptionActive) {
      return {
        canCreate: false,
        disabledMessage: `Approve billing to create this ${category}`,
      };
    }

    const missingTestTypes: string[] = [];
    const hasNecessaryScopes = Object.entries(experience.testTypes).every(
      ([key, value]) => {
        if (value) {
          const scopeKey =
            CreateTestTypeMap[key as keyof ExperienceTestTypeDto];
          const result = scopeMap[scopeKey];
          if (!result) {
            missingTestTypes.push(
              FriendlyTestTypeMap[key as keyof ExperienceTestTypeDto]
            );
          }
          return result;
        }
        return true;
      }
    );

    if (!hasNecessaryScopes) {
      return {
        canCreate: false,
        disabledMessage: `Upgrade your plan to create this ${missingTestTypes.join(", ")} ${category}`,
      };
    }

    return {
      canCreate: true,
      disabledMessage: "",
    };
  }, [experience, isSubscriptionActive, scopeMap, isPriceTestIntegrationReady]);
};
